import React, { useMemo, Fragment } from 'react';
import T from 'prop-types';
import { every, first, forEach, isEmpty, keys, map } from 'lodash-es';

import ChartTable from './ChartTable';
import Button from '../../../../components/Buttons/Button';
import { SmartChart } from '../../../../components/Chart';
import Skeleton from '../../../../components/Skeleton';

import { chartCohortsYearConfig, formatByGranularity } from '../config/finance/cohorts-charts';
import { ISODateFormat, format, yesterday, isBefore } from '../../../../utils/date';
import { ADVANCED_METRICS_PERCENTAGE, COHORTS } from '../../../../utils/advanced-metrics';

const emptyObject = {};

const seriesColors = {
  0: '#348BE3',
  1: '#E1E5EB',
};

const isSeriesFormatValid = (series, cohort, metric) => Array.isArray(series?.[cohort]?.[metric]);

const isSeriesEmpty = series => every(series, seriesData => isEmpty(seriesData)
  || every(seriesData, ([, value]) => value === null));

const YearCohortsChart = ({
  loading,
  series,
  metric,
  cohort,
  granularity,
  overPeriod,
  rotateLabels,
  height = 276,
  skeletonHeight = 276,
  animation,
  hasTable = false,
  yGridLine = false,
  yAxisZeroMiddle,
  variant,
  showTable,
  hideSinglePoinLegend,
  toggleSize = 'sm',
  maxDate,
  subDates,
  onShowTable,
}) => {
  const activeCohorts = useMemo(() => ['all_users', cohort], [cohort]);

  const seriesData = useMemo(() => {
    const decoratedSeries = activeCohorts.reduce((result, item) => ({
      ...result,
      [item]: series?.[item]?.[`${metric}_change`] || [],
    }), {});

    return isSeriesEmpty(decoratedSeries) ? emptyObject : decoratedSeries;
  }, [series, activeCohorts, metric]);

  const tableData = useMemo(() => {
    if (!isSeriesFormatValid(series, cohort, metric)) {
      return {
        headerLabels: emptyObject,
        leadColumnLabels: emptyObject,
        rows: emptyObject,
      };
    }

    const unitsByMetric = ADVANCED_METRICS_PERCENTAGE.reduce((acc, item) => ({ ...acc, [item]: '%' }), {});

    return ({
      headerLabels: series.all_users[metric].reduce((result, [timestamp]) => ({
        ...result,
        [timestamp]: formatByGranularity(timestamp, granularity),
      }), {}),
      leadColumnLabels: activeCohorts.reduce((result, item) => ({
        ...result,
        [item]: COHORTS[item],
        [`${item}_change`]: '% Change',
      }), {}),
      rows: activeCohorts.reduce((result, item) => ({
        ...result,
        [item]: series[item]?.[metric] || [],
        [`${item}_change`]: series[item]?.[`${metric}_change`] || [],
      }), {}),
      valueUnits: activeCohorts.reduce((result, item) => ({
        ...result,
        [item]: unitsByMetric[metric] || '',
        [`${item}_change`]: '%',
      }), {}),
    });
  }, [series, activeCohorts, cohort, metric, granularity]);

  const filters = useMemo(() => {
    let periodFrom = maxDate || yesterday();

    forEach(seriesData, (data) => {
      const firstDate = first(data)?.[0];

      if (firstDate && isBefore(firstDate, periodFrom)) {
        periodFrom = format(firstDate, ISODateFormat);
      }
    });

    return ({
      granularity,
      period_from_date: periodFrom,
      period_to_date: maxDate || yesterday(),
    });
  }, [seriesData, granularity, maxDate]);

  const chartConfig = useMemo(() => (
    chartCohortsYearConfig(seriesData, {
      granularity,
      overPeriod,
      rotateLabels,
      height,
      animation,
      yGridLine,
      yAxisZeroMiddle,
      variant,
      maxDate,
      subDates,
    })
  ), [seriesData, granularity, overPeriod, subDates]);

  const metaSeriesData = useMemo(() => Object.keys(seriesData).reduce((result, item, index) => ({
    ...result,
    [item]: {
      color: seriesColors[item] || seriesColors[index],
      type: 'column',
      visible: true,
      name: COHORTS[item],
      cohort: item,
      // decorateColor: ({ y }) => (y >= 0 ? '#3E8EDE' : '#E33963'), // for partial period
    },
  }), {}), [seriesData]);

  const showLegend = !hideSinglePoinLegend
    || (hideSinglePoinLegend && keys(metaSeriesData).length > 1);

  return (
    <div className="year-cohorts-chart-wrapper">
      <div className="year-cohorts-chart-wrapper-box">
        {loading
          ? <Skeleton height={skeletonHeight} />
          : (
            <>
              <SmartChart
                chartId="year-chart"
                series={seriesData}
                metaSeriesData={metaSeriesData}
                config={chartConfig}
                filters={filters}
                allowPartialPeriods
              />
              {showLegend && (
                <div className="year-cohorts-chart-legend">
                  {map(metaSeriesData, ({ color, name }) => (
                    <div key={name} className="year-cohorts-chart-legend-item">
                      <span className="marker" style={{ backgroundColor: color }} />
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
      </div>
      {hasTable && (
        <Fragment>
          <div className="table-toggle-wrapper">
            {!loading && (
              <Button
                size={toggleSize || undefined}
                shape="square"
                variant="secondary"
                className="table-toggle-button"
                active={showTable}
                onClick={onShowTable}
              >
                <i className="hicon-list-button" />
              </Button>
            )}
          </div>
          <ChartTable
            show={showTable}
            loading={loading}
            variant="cohortYear"
            tableData={tableData}
          />
        </Fragment>
      )}
    </div>
  );
};

YearCohortsChart.propTypes = {
  loading: T.bool,
  series: T.object,
  metric: T.string,
  cohort: T.string,
  granularity: T.string,
  overPeriod: T.string,
  rotateLabels: T.bool,
  height: T.number,
  skeletonHeight: T.number,
  animation: T.bool,
  hasTable: T.bool,
  yGridLine: T.bool,
  yAxisZeroMiddle: T.bool,
  variant: T.oneOf(['qv', 'advanced']),
  showTable: T.bool,
  toggleSize: T.string,
  hideSinglePoinLegend: T.bool,
  maxDate: T.string,
  subDates: T.object,
  onShowTable: T.func,
};

export default YearCohortsChart;
